import React from "react";
import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, FileJson, Search } from "lucide-react";
import { useCbomContext } from "../context/CbomContext";
import { fetchKeys } from "../services/apiServices";
import JsonModal from "./JsonModal";
import { sortData } from "../utils/sortData";
import { CbomKeys } from "@schemas";
import { useProjectContext } from "context/ProjectContext";

interface CbomKey {
  k_id: number;
  device_id: string;
  file_path: string;
  key_type: string;
  algorithm: string;
  key_length: number;
  curve_name: string;
  format: string;
  quantum_safe_status: string;
  // target_algorithm: string;
  // compliance_status: string;
  cbom_json: any;
}
const cbomKeyFieldLabels: Record<keyof CbomKey, string> = {
  k_id: "Key ID",
  device_id: "Device ID",
  file_path: "File Path",
  key_type: "Key Type",
  algorithm: "Algorithm",
  key_length: "Key Length",
  curve_name: "Curve Name",
  format: "Format",
  quantum_safe_status: "Quantum Safe Status",
  // target_algorithm: "Target Algorithm",
  // compliance_status: "Compliance Status",
  cbom_json: "CBOM JSON",
};

interface KeysTableProps {
  customData?: CbomKeys[];
}
const KeysTable: React.FC<KeysTableProps> = ({ customData }) => {
  const { selectedDeviceId, showCbomComponent } = useCbomContext();
  const cbomKeyFields = Object.keys(cbomKeyFieldLabels) as (keyof CbomKey)[];
  const [keysData, setKeysData] = useState<CbomKey[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(5);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredSources, setFilteredSources] = useState<CbomKey[]>([]);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredSources.slice(indexOfFirstItem, indexOfLastItem);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [jsonData, setJsonData] = useState(null);
  const [sortField, setSortField] = useState<keyof CbomKey>("k_id");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };
  const { state } = useProjectContext();
  console.log(state);
  useEffect(() => {
    if (searchTerm) {
      const filtered = keysData.filter(
        (e) =>
          e &&
          Object.values(e)
            .filter(
              (value) => typeof value === "string" || typeof value === "number"
            )
            .join(" ")
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
      setFilteredSources(filtered);
    } else {
      setFilteredSources(keysData); // Show all data if search term is cleared
    }
  }, [searchTerm, keysData]);
  const totalPages = Math.ceil(filteredSources.length / itemsPerPage);

  const nextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };
  const prevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };
  useEffect(() => {
    if (customData) {
      const transformedData = customData.map((item) => ({
        k_id: item.k_id ?? 0,
        device_id: item.device_id ?? "",
        file_path: item.file_path ?? "",
        key_type: item.key_type ?? "",
        algorithm: item.algorithm ?? "",
        key_length: item.key_length ?? 0,
        curve_name: item.curve_name ?? "",
        format: item.format ?? "",
        quantum_safe_status: item.quantum_safe_status ?? "",
        cbom_json: item.cbom_json ?? {},
      }));
      const sorted = sortData<CbomKey>(transformedData, "k_id", "asc");
      setKeysData(sorted);
    } else {
      if (state.currentProjectID) {
        fetchKeys(
          (data) => {
            const sorted = sortData<CbomKey>(data, "k_id", "asc");
            setKeysData(sorted);
          },
          selectedDeviceId,
          showCbomComponent,
          state.currentProjectID
        );
      }
    }
  }, [selectedDeviceId, showCbomComponent, state.currentProjectID]);
  useEffect(() => {
    if (sortField) {
      const sortedData = sortData(filteredSources, sortField, sortOrder);
      setFilteredSources(sortedData);
    }
  }, [sortField, sortOrder]);
  const handleJsonClick = (json: any) => {
    setJsonData(json);
    setIsModalOpen(true);
  };
  const handleSort = (field: keyof CbomKey) => {
    // console.log("Field clicked:", field);
    // console.log("Current sortOrder:", sortOrder);
    if (sortField === field) {
      setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };
  return (
    <>
      <div className="flex flex-col h-full bg-custom-blue8 border rounded-md p-2">
        <div className="flex justify-between items-center mb-4">
          {/* <h1 className="text-xl font-bold text-custom-blue7 pl-2">
            Keys Information
          </h1> */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={handleSearch}
              className="pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-custom-blue5 bg-white"
            />
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
          </div>
          {location.hash === "#/overallSummary" && <div></div>}
        </div>
        <div className="flex-1 overflow-auto custom-scrollbar">
          <table
            id="data-table"
            className="min-w-full bg-white shadow custom-scrollbar border rounded-md"
          >
            <thead className="sticky top-0 z-10 bg-custom-blue0">
              <tr className="bg-custom-blue0 text-sm text-left text-custom-blue7">
                {cbomKeyFields.map((header) => (
                  <th
                    key={header}
                    className={`p-2 ${
                      header === "file_path"
                        ? "max-w-[200px] whitespace-normal break-words"
                        : ""
                    }`}
                    onClick={() => handleSort(header)}
                  >
                    {cbomKeyFieldLabels[header]}
                    {sortField === header && (
                      <span className="ml-1">
                        {sortOrder === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentItems &&
              Array.isArray(currentItems) &&
              currentItems.length > 0 ? (
                currentItems.map((source, index) => {
                  return (
                    <tr
                      key={source.k_id}
                      className={`${
                        index % 2 === 1 ? "bg-custom-blue0" : ""
                      } hover:bg-custom-blue0 text-sm text-custom-blue7`}
                    >
                      {cbomKeyFields.map((field) => (
                        <td
                          key={field}
                          className={`p-2 ${
                            ["file_path"].includes(field)
                              ? "max-w-[200px] break-words whitespace-normal"
                              : ""
                          }`}
                        >
                          {field === "cbom_json" && source.cbom_json ? (
                            <button
                              className="text-blue-600 hover:text-custom-blue7 flex items-center gap-1"
                              onClick={() => handleJsonClick(source.cbom_json)}
                            >
                              <FileJson />
                            </button>
                          ) : source[field] ? (
                            String(source[field])
                          ) : (
                            "N/A"
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={15}
                    className="text-custom-blue7 p-2 text-center"
                  >
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex justify-between items-center mt-2">
          <div className="flex items-center space-x-2">
            <select
              className="border rounded-md p-2 bg-white"
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
              <option value={40}>40</option>
              <option value={50}>50</option>
            </select>
            <span className="text-custom-blue7">Results per page</span>
          </div>
          <div className="flex items-center space-x-2 text-custom-blue7">
            <span>
              Displaying {indexOfFirstItem + 1}-
              {Math.min(indexOfLastItem, filteredSources.length)} of{" "}
              {filteredSources.length}
            </span>
            <button
              className="p-2 bg-white rounded-md"
              onClick={prevPage}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={20} />
            </button>
            <button
              className="p-2 bg-white rounded-md"
              onClick={nextPage}
              disabled={currentPage === totalPages}
            >
              <ChevronRight size={20} />
            </button>
          </div>
          {isModalOpen && (
            <JsonModal
              data={jsonData}
              onClose={() => setIsModalOpen(false)}
              title="CBOM Details"
            />
          )}
        </div>
      </div>
    </>
  );
};

export default KeysTable;
